import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { login, isAuthenticated } from '../../services/authService';
import './LoginPage.css';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showVerificationForm, setShowVerificationForm] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  useEffect(() => {
    // Check if already authenticated
    const checkAuth = async () => {
      const authenticated = await isAuthenticated();
      if (authenticated) {
        navigate('/');
      } else {
        setLoading(false);
      }
    };
    checkAuth();
  }, [navigate]);

  const handleGoogleLogin = async () => {
    try {
      await login();
    } catch (error) {
      console.error('Google login error:', error);
      setError(t('login.googleLoginError'));
    }
  };

  const handleEmailPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      if (isSignUp) {
        const { signUpWithEmail } = await import('../../services/authService');
        await signUpWithEmail(email, password);
        setShowEmailForm(false);
        setShowVerificationForm(true);
        setPassword(''); // Clear password for security
      } else {
        const { signInWithEmail } = await import('../../services/authService');
        await signInWithEmail(email, password);
        navigate('/');
      }
    } catch (err: any) {
      console.error('Email/password error:', err);
      setError(err.message || t('login.authFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const { confirmSignUpCode } = await import('../../services/authService');
      await confirmSignUpCode(email, verificationCode);

      // Show success message and redirect to sign in
      alert(t('login.emailVerifiedSuccess'));
      setShowVerificationForm(false);
      setShowEmailForm(true);
      setIsSignUp(false);
      setVerificationCode('');
    } catch (err: any) {
      console.error('Verification error:', err);
      setError(err.message || t('login.verificationFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="login-page">
        <div className="login-container">
          <p>{t('login.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>{t('login.title')}</h1>
          <p>{t('login.description')}</p>
        </div>

        {showVerificationForm ? (
          <form className="email-form" onSubmit={handleVerificationSubmit}>
            <h3>{t('login.verifyYourEmail')}</h3>
            <p className="form-hint" dangerouslySetInnerHTML={{ __html: t('login.verificationHint', { email }) }} />

            {error && <div className="error-message">{error}</div>}

            <input
              type="text"
              placeholder={t('login.verificationCode')}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              required
              disabled={submitting}
              maxLength={6}
            />

            <button type="submit" className="submit-button" disabled={submitting}>
              {submitting ? t('login.verifying') : t('login.verifyEmail')}
            </button>

            <button
              type="button"
              className="back-button"
              onClick={() => {
                setShowVerificationForm(false);
                setShowEmailForm(false);
                setError(null);
                setVerificationCode('');
              }}
              disabled={submitting}
            >
              {t('login.backToLoginOptions')}
            </button>
          </form>
        ) : !showEmailForm ? (
          <>
            <button className="google-login-button" onClick={handleGoogleLogin}>
              <svg className="google-icon" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {t('login.signInWithGoogle')}
            </button>

            <div className="divider">
              <span>{t('login.or')}</span>
            </div>

            <button
              className="email-login-button"
              onClick={() => setShowEmailForm(true)}
            >
              {t('login.signInWithEmail')}
            </button>
          </>
        ) : (
          <form className="email-form" onSubmit={handleEmailPasswordSubmit}>
            <h3>{isSignUp ? t('login.createAccount') : t('login.signIn')}</h3>

            {error && <div className="error-message">{error}</div>}

            <input
              type="email"
              placeholder={t('login.email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={submitting}
            />

            <input
              type="password"
              placeholder={t('login.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={submitting}
            />

            <button type="submit" className="submit-button" disabled={submitting}>
              {submitting ? t('login.processing') : (isSignUp ? t('login.signUp') : t('login.signIn'))}
            </button>

            <button
              type="button"
              className="toggle-button"
              onClick={() => setIsSignUp(!isSignUp)}
              disabled={submitting}
            >
              {isSignUp ? t('login.alreadyHaveAccount') : t('login.needAccount')}
            </button>

            <button
              type="button"
              className="back-button"
              onClick={() => {
                setShowEmailForm(false);
                setError(null);
              }}
              disabled={submitting}
            >
              {t('login.backToOptions')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
